import chunk from 'lodash/chunk'
import { v4 } from 'uuid'

import { AppError } from '@app/errors/AppError'
import { displayMessage } from '@app/errors/displayMessage'

import { createSelector } from '@app/utils/createSelector'
import { omit } from '@app/utils/omit'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiIgnoredUser } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import type { ParentSubscriptionStatus } from '@app/components/ParentSubscriptionStatusSelect/types'

import { createIsUserFavoriteSelector } from './favorites'
import { usersModelsSelector as usersSelector } from './users'

const ignoredSlice = createReduxSlice<{ hash: string; list: ApiIgnoredUser[]; error: string | null }>('ignored')
const ignoredSyncSlice = createReduxSlice<{ hash: string }>('ignored_sync')

/** Ignoring, unignoring or favoriting somebody all invalidate the list the selector holds. */
export const invalidateIgnoredSlice = () =>
  createThunk(dispatch => {
    // The error goes with it: it belongs to the read being replaced, and would otherwise sit over
    // the list until the new one lands.
    dispatch(ignoredSlice.update(state => (state?.error ? { ...state, error: null } : state)))
    dispatch(ignoredSyncSlice.set({ hash: v4() }))
  })

const ignoredPending = createReduxSlice<Record<string, boolean>>('ignored_pending')

/** Shows the row in its new state while the call is in flight. */
export const setIgnoredPendingSlice = (userId: string, ignored: boolean) => ignoredPending.update(state => ({ ...(state ?? {}), [userId]: ignored }))

export const clearIgnoredPendingSlice = (userId: string) => ignoredPending.update(state => omit(state ?? {}, userId))

const ignoredPendingSelector = createSelector([ignoredPending.selector], pending => pending ?? {})

const ignoredModelsSlice = createReduxSlice<{ hash: string }>('ignored_models')

export const ignoredIdsResultSelector = createSelector(
  [ignoredSlice.selector, ignoredSyncSlice.selector, profileUserResultSelector],
  (slice, sync, profileResult) =>
    createLoadable(c => {
      const profile = c.unwrap(profileResult)
      if (profile?.account_type !== 'sitter') return []
      const hash = [profile.id, sync?.hash].join(':')
      if (!slice || slice.hash !== hash) {
        return c.load(
          createPromise('Ignored users ids must be fetched', hash, async dispatch => {
            await dispatch(loadIgnoredUsers(hash))
          })
        )
      }
      return slice.list.map(i => i.attributes.user_id)
    })
)

/** Why the last read failed, for the page to show over whatever list is still on screen. */
export const ignoredErrorSelector = createSelector([ignoredSlice.selector], slice => slice?.error ?? null)

export const createIsUserIgnoredResultSelector = (userIdSelector: (...args: any[]) => string | null) =>
  createSelector([userIdSelector, ignoredPendingSelector, ignoredIdsResultSelector], (id, pending, idsResult) =>
    createLoadable(c => {
      if (!id) return false
      const pendingValue = pending[id]
      if (typeof pendingValue === 'boolean') return pendingValue
      const ids = c.unwrap(idsResult)
      return ids.includes(id)
    })
  )

export const ignoredUsersResultSelector = createSelector([ignoredModelsSlice.selector, ignoredIdsResultSelector, usersSelector], (slice, idsResult, users) =>
  createLoadable(c => {
    const ids = c.unwrap(idsResult)
    if (!ids.length) return []
    const hash = ids.toSorted().join(':')
    if (!slice || slice.hash !== hash) {
      return c.load(
        createPromise('Ignored users must be fetched', hash, async dispatch => {
          const [{ getUsersByFilter }, { callV2Api }, { setUsersFromResponseSlice }] = await Promise.all([
            import('@app/packages/api/v2/actions/users'),
            import('@app/store/actions/api'),
            import('@app/store/slices/users'),
          ])
          const idsChunks = chunk(ids, 10)
          const users = await Promise.all(
            idsChunks.map(async ids => {
              const resp = await dispatch(callV2Api(getUsersByFilter({ ids, account_type: 'parent' })))
              dispatch(setUsersFromResponseSlice(resp))
              return resp
            })
          ).then(responses => responses.flatMap(r => r.data))
          const pids = users.map(u => u.id)
          for (const id of ids) {
            if (!pids.includes(id)) {
              throw new AppError(`Ignored user ${id} is not a parent`).withExpose(true)
            }
          }
          dispatch(ignoredModelsSlice.set({ hash }))
        })
      )
    }
    return ids.map(id => {
      const user = users[id]
      if (!user) throw new AppError(`Ignored user ${id} not found`).withExpose(true)
      return user
    })
  })
)

export const createSubscriptionStatusResultSelector = (idSelector: (...args: any[]) => string | null) =>
  createSelector([createIsUserFavoriteSelector(idSelector), createIsUserIgnoredResultSelector(idSelector)], (isFavorite, isIgnoredResult) =>
    createLoadable<ParentSubscriptionStatus>(c => {
      if (isFavorite) return 'favorite'
      const isIgnored = c.unwrap(isIgnoredResult)
      return isIgnored ? 'ignored' : null
    })
  )

/**
 * The load the selector waits on, which does not fail. A read that could not be made is stamped
 * with the hash it was asked for anyway, so the selector resolves to whatever was already loaded
 * rather than blanking the page, and the failure rides in the slice for the list to show above it.
 */
const loadIgnoredUsers = (hash: string) =>
  createThunk(async (dispatch, getState) => {
    try {
      dispatch(ignoredSlice.set({ hash, list: await dispatch(getAllIgnoredUsers()), error: null }))
    } catch (error) {
      // Nothing loaded before means nothing to fall back on: the failure is the whole answer, and
      // it goes to the boundary rather than into a slice that would read as an empty list.
      const loaded = ignoredSlice.selector(getState())
      if (!loaded) throw error
      dispatch(ignoredSlice.set({ ...loaded, hash, error: displayMessage(error) }))
    }
  })

const getAllIgnoredUsers = () =>
  createThunk(async dispatch => {
    const [{ getIgnoredUsers }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/ignored_users'), import('@app/store/actions/api')])
    let page = 1
    const result: ApiIgnoredUser[] = []
    while (true) {
      const resp = await dispatch(callV2Api(getIgnoredUsers({ page }))).catch(catchFetchDataError(dispatch, 'Ignored fetch failed'))
      result.push(...resp.data)
      if (page >= resp.meta.total_pages) break
      page += 1
    }
    return result
  })
